import { useState, useEffect, useCallback, useRef } from 'react';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Box, Button, Typography, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, IconButton, TablePagination, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid, Snackbar, Slide } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import { settingsPageTranslationRef } from '../../translation/settings-page/settings-page';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { Alert, Autocomplete } from '@material-ui/lab';

const DelegationsTable = () => {
  const identityApi = useApi(identityApiRef);
  const config = useApi(configApiRef);
  const catalogApi = useApi(catalogApiRef);
  const backendUrl = config.getString('backend.baseUrl');
  const { t } = useTranslationRef(settingsPageTranslationRef);
  const [delegations, setDelegations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [newDelegate, setNewDelegate] = useState('');

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [users, setUsers] = useState<any[]>([]);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // 🔥 Estado para el mensaje dinámico
  const [alertInfo, setAlertInfo] = useState<{
    type: 'warning' | 'success' | 'error' | 'info';
    message: string;
  } | null>(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  function SlideTransition(props: any) {
    return <Slide {...props} direction="up" />;
  }

  const showAlert = (
    type: 'warning' | 'success' | 'error' | 'info',
    message: string,
  ) => {
    setSnackbarOpen(false);      // cerrar inmediatamente
    setAlertInfo(null);          // resetear estado

    // reabrir en el siguiente ciclo de render
    setTimeout(() => {
      setAlertInfo({ type, message });
      setSnackbarOpen(true);
    }, 50);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // Cargar solo delegaciones del usuario actual
  const loadDelegations = useCallback(async () => {
    setLoading(true);
    try {
      const identity = await identityApi.getBackstageIdentity();
      const userEntityRef = identity.userEntityRef;
      let userEmail = userEntityRef.split('/')[1];
      try {
        const userEntity = await catalogApi.getEntityByRef(userEntityRef);
        const profile = userEntity?.spec?.profile as any;
        console.log("Profile:", profile);
        if (profile?.email) userEmail = profile.email;
      } catch { }
      // Impersonación
      const impersonatedFromStorage = localStorage.getItem('backstage.impersonatedUser');
      const impersonatedFromConfig = config.getOptionalString('openfga.impersonatedUser');
      const impersonatedUser = impersonatedFromStorage || impersonatedFromConfig;
      let effectiveUser = impersonatedUser || userEmail;
      if (impersonatedUser && impersonatedUser !== userEmail) {
        try {
          const impersonatedUserRef = `user:default/${impersonatedUser}`;
          const impersonatedUserEntity = await catalogApi.getEntityByRef(impersonatedUserRef);
          const impersonatedProfile = impersonatedUserEntity?.spec?.profile as any;
          if (impersonatedProfile?.email) effectiveUser = impersonatedProfile.email;
        } catch { }
      }
      const token = (await identityApi.getCredentials()).token;
      // Buscar delegaciones usando list-user-entities para el usuario logueado y relation 'delegate'
      const response = await fetch(`${backendUrl}/api/openfga-client/list-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          objectType: 'user',
          objectId: effectiveUser,
          relation: 'delegate',
          userFilters: [{ type: 'user' }]
        }),
      });
      const data = await response.json();
      // Los objects devueltos son los users delegados
      const delegations = data.users.map((user: any) => user.id) || [];
      setDelegations(delegations || []);
    } catch (err: any) {
      showAlert('error', "Error cargando delegaciones");
    } finally {
      setLoading(false);
    }
  }, [identityApi, config]);

  useEffect(() => { loadDelegations(); }, [loadDelegations]);

  // Añadir delegado
  const handleAdd = async () => {
    if (!newDelegate) { showAlert('error', "selecciona un delegado"); return; }
    try {
      const identity = await identityApi.getBackstageIdentity();
      const userEntityRef = identity.userEntityRef;
      let userEmail = userEntityRef.split('/')[1];
      try {
        const userEntity = await catalogApi.getEntityByRef(userEntityRef);
        const profile = userEntity?.spec?.profile as any;
        if (profile?.email) userEmail = profile.email;
      } catch { }
      // Impersonación
      const impersonatedFromStorage = localStorage.getItem('backstage.impersonatedUser');
      const impersonatedFromConfig = config.getOptionalString('openfga.impersonatedUser');
      const impersonatedUser = impersonatedFromStorage || impersonatedFromConfig;
      let effectiveUser = impersonatedUser || userEmail;
      if (impersonatedUser && impersonatedUser !== userEmail) {
        try {
          const impersonatedUserRef = `user:default/${impersonatedUser}`;
          const impersonatedUserEntity = await catalogApi.getEntityByRef(impersonatedUserRef);
          const impersonatedProfile = impersonatedUserEntity?.spec?.profile as any;
          if (impersonatedProfile?.email) effectiveUser = impersonatedProfile.email;
        } catch { }
      }
      const token = (await identityApi.getCredentials()).token;
      const tuple = {
        user: `user:${newDelegate}`,
        relation: 'delegate',
        object: `user:${effectiveUser}`,
      };
      const response = await fetch(`${backendUrl}/api/openfga-client/add-tuple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(tuple),
      });
      if (!response.ok) throw new Error('No se pudo añadir el delegado');
      showAlert('success', "Delegado añadido correctamente");
      setOpenDialog(false);
      setNewDelegate('');
      loadDelegations();
    } catch (err: any) {
      showAlert('error', "Error añadiendo delegado");
    }
  };

  // Eliminar delegado
  const handleDelete = async (tuple: any) => {
    try {
      const identity = await identityApi.getBackstageIdentity();
      const userEntityRef = identity.userEntityRef;
      let userEmail = userEntityRef.split('/')[1];
      try {
        const userEntity = await catalogApi.getEntityByRef(userEntityRef);
        const profile = userEntity?.spec?.profile as any;
        if (profile?.email) userEmail = profile.email;
      } catch { }
      // Impersonación
      const impersonatedFromStorage = localStorage.getItem('backstage.impersonatedUser');
      const impersonatedFromConfig = config.getOptionalString('openfga.impersonatedUser');
      const impersonatedUser = impersonatedFromStorage || impersonatedFromConfig;
      let effectiveUser = impersonatedUser || userEmail;
      if (impersonatedUser && impersonatedUser !== userEmail) {
        try {
          const impersonatedUserRef = `user:default/${impersonatedUser}`;
          const impersonatedUserEntity = await catalogApi.getEntityByRef(impersonatedUserRef);
          const impersonatedProfile = impersonatedUserEntity?.spec?.profile as any;
          if (impersonatedProfile?.email) effectiveUser = impersonatedProfile.email;
        } catch { }
      }
      const body = {
        user: `user:${tuple}`,
        relation: 'delegate',
        object: `user:${effectiveUser}`,
      }
      const token = (await identityApi.getCredentials()).token;
      const response = await fetch(`${backendUrl}/api/openfga-client/remove-tuple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error('No se pudo eliminar el delegado');
      showAlert('success', "Delegado eliminado correctamente");
      loadDelegations();
    } catch (err: any) {
      showAlert('error', "Error eliminando delegado");
    }
  };

  const fetchCatalogUsersPage = async (query: string, signal?: AbortSignal) => {
    try {
      const token = (await identityApi.getCredentials()).token;
      const url = query.trim() === ''
        ? `${backendUrl}/api/catalog/entities/by-query?limit=50&offset=0&orderField=metadata.name%2Casc&filter=kind%3Duser&fullTextFilterFields=metadata.name,metadata.title,spec.profile.displayName,spec.profile.email`
        : `${backendUrl}/api/catalog/entities/by-query?limit=50&offset=0&orderField=metadata.name%2Casc&filter=kind%3Duser&fullTextFilterTerm=${query}&fullTextFilterFields=metadata.name,metadata.title,spec.profile.displayName,spec.profile.email`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        signal,
      });

      const items: any = await response.json();

      return items.items;
    } finally {
    }
  };

  const handleSearchInput = (query: string) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (abortControllerRef.current) abortControllerRef.current.abort();

    if (query.trim().length < 3) {
      setUsers([]); // limpia buffer si < 3 letras
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const controller = new AbortController();
        abortControllerRef.current = controller;

        const items = await fetchCatalogUsersPage(query, controller.signal);
        setUsers(items);  // solo reemplaza buffer, MUI mantiene foco
      } catch (e: any) {
        if (e.name === 'AbortError') return;
        console.error('[PTM] fetchCatalogUsersPage error', e);
        setUsers([]);
      }
    }, 300);
  };

  return (
    <Box>
      {alertInfo && snackbarOpen && (
        <Snackbar
          open={snackbarOpen}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          TransitionComponent={SlideTransition}
          autoHideDuration={2500}
        >
          <Alert onClose={handleCloseSnackbar} severity={alertInfo.type} variant="filled">
            {alertInfo.message}
          </Alert>
        </Snackbar>
      )}
      <Grid container spacing={0} direction="row" alignItems="center" justifyContent="space-between" style={{ marginTop: 5 }}>
        <Typography variant="h6" gutterBottom>{t("permissions.delegations.title")}</Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
          {t("permissions.delegations.addButton")}
        </Button>
      </Grid>
      <TableContainer component={Paper} style={{ marginTop: 16 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("permissions.delegations.table.delegate")}</TableCell>
              <TableCell align='right'>{t("permissions.delegations.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={2}>{t("permissions.delegations.loading")}</TableCell></TableRow>
            ) : delegations.length === 0 ? (
              <TableRow><TableCell colSpan={2}>{t("permissions.delegations.notFound")}</TableCell></TableRow>
            ) : (
              delegations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((tuple, idx) => (
                <TableRow key={idx}>
                  <TableCell>{tuple.replace('user:', '')}</TableCell>
                  <TableCell align='right'>
                    <IconButton size="small" onClick={() => handleDelete(tuple)} title="Eliminar delegado">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={delegations.length}
        page={page}
        onPageChange={(_e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50]}
      />
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("permissions.delegations.addButton")}</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={users}
            getOptionLabel={opt => opt?.spec?.profile?.displayName || ''}
            filterOptions={(x) => x}      // desactiva filtro interno
            onInputChange={(_, value, reason) => {
              if (reason !== 'input') return;
              handleSearchInput(value);   // debounce + mínimo 3 letras
            }}
            clearOnBlur={false}          // no borrar texto al perder foco
            blurOnSelect={false}         // no perder foco al seleccionar
            openOnFocus                  // mantiene dropdown abierto
            onChange={(_, newValue) => {
              if (newValue) setNewDelegate(`${newValue?.spec?.profile?.email}`);
            }}
            renderInput={params => (
              <TextField
                {...params}
                label="Delegado"
                required
              />
            )}
            renderOption={(option) => (
              <Box>
                <Typography variant="body2">{option?.spec?.profile?.displayName}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {option?.spec?.profile?.email || ''}
                </Typography>
              </Box>
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button onClick={handleAdd} color="primary" variant="contained">Añadir</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DelegationsTable;
